// Derives a URL/id-safe slug from arbitrary free text (e.g. a dashboard or
// layout title) so it can be used as an internal key. This is lossy and
// silent by design: case, accents, whitespace, and any other disallowed
// character are rewritten or dropped without surfacing an error, because the
// caller only needs *a* valid slug, not one that round-trips back to the
// original text. Do not use this for a field where the user directly authors
// and expects to see validation feedback on the exact slug value; use
// `normalizeSlugInput` for that instead.
export function normalizeToSlug(input: string): string {
  return (
    input
      .normalize('NFD') // Normalize to decompose accented characters
      // biome-ignore lint/suspicious/noMisleadingCharacterClass: These are special diacritical characters
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
      .toLowerCase() // Convert to lowercase
      .trim() // Trim leading and trailing whitespace
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/[^a-z0-9_-]/g, '') // Remove any character that's not alphanumeric, hyphen, or underscore
  );
}

// Paste sources (Word, Slack, terminals, etc.) commonly substitute lookalike
// dash characters (en/em dash, minus sign, fullwidth hyphen, ...) or carry
// invisible characters (zero-width space, BOM, soft hyphen, ...) that a typed
// value never has. This normalizes only those clipboard artifacts so a pasted
// value validates identically to the same value typed by hand; it otherwise
// preserves the input verbatim (case, spacing, accents, symbols) so that any
// genuinely invalid character still surfaces a validation error to the user.
// Use this for slug fields the user directly authors/pastes into and expects
// to be validated as-is; use `normalizeToSlug` instead when you need to
// auto-derive a best-effort slug from unrelated free text (e.g. a title).
// Code points are spelled out numerically rather than as literal characters,
// since the characters themselves are invisible or easily confused.
const DASH_LOOKALIKE_CODE_POINTS = [
  0x2010, // hyphen
  0x2011, // non-breaking hyphen
  0x2012, // figure dash
  0x2013, // en dash
  0x2014, // em dash
  0x2015, // horizontal bar
  0x2212, // minus sign
  0x2043, // hyphen bullet
  0xfe58, // small em dash
  0xfe63, // small hyphen-minus
  0xff0d, // fullwidth hyphen-minus
];
const INVISIBLE_CODE_POINTS = [
  0x200b, // zero width space
  0x200c, // zero width non-joiner
  0x200d, // zero width joiner
  0xfeff, // BOM / zero width no-break space
  0x00ad, // soft hyphen
];

const toCharClass = (codePoints: number[]) =>
  codePoints.map((cp) => String.fromCodePoint(cp)).join('');

const DASH_LOOKALIKES = new RegExp(
  `[${toCharClass(DASH_LOOKALIKE_CODE_POINTS)}]`,
  'g',
);
const INVISIBLE_CHARS = new RegExp(
  `[${toCharClass(INVISIBLE_CODE_POINTS)}]`,
  'g',
);

export const normalizeSlugInput = (value: string) =>
  value.replace(DASH_LOOKALIKES, '-').replace(INVISIBLE_CHARS, '').trim();
